import React from 'react';
import { format } from 'date-fns';
import { usePoolData } from '../hooks/usePoolData.js';
import { useTimeElapsed } from '../hooks/useTimeElapsed.js';
import { formatTimeInterval, formatProbability } from '../utils/poissonCalculations.js';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';
import { MetricTooltip } from './MetricTooltip.jsx';

function StatsPanelComponent() {
  const { poolInfo, analysis, predictionData, lastRefresh, blocks, backtestResults } = usePoolData();
  
  const [showAccuracy, setShowAccuracy] = React.useState(true);
  
  // Get the most recent block timestamp as fallback if poolInfo.lastBlockTimestamp is missing
  const latestBlock = blocks && blocks.length > 0 
    ? blocks.reduce((latest, block) => {
        if (!latest) return block;
        // Prefer highest timestamp, fallback to highest height
        if (block.timestamp > latest.timestamp) return block;
        if (block.timestamp === latest.timestamp && block.height > latest.height) return block;
        return latest;
      })
    : null;
  
  // Use poolInfo.lastBlockTimestamp if available, otherwise use latest block's timestamp
  const lastBlockTimestamp = poolInfo?.lastBlockTimestamp && poolInfo.lastBlockTimestamp > 0
    ? poolInfo.lastBlockTimestamp
    : (latestBlock?.timestamp || 0);
  
  const elapsedTime = useTimeElapsed(lastBlockTimestamp);

  if (!poolInfo || !analysis) {
    return (
      <div className="bg-navy-light border border-dark p-6 rounded">
        <p className="text-primary">No statistics available</p>
      </div>
    );
  }

  // Calculate estimated time left (can be negative if overdue)
  const estimatedTimeLeft = analysis.expectedTime - elapsedTime.seconds;
  const estimatedTimeLeftFormatted = formatTimeInterval(Math.abs(estimatedTimeLeft));
  const isOverdue = estimatedTimeLeft < 0;

  // Get network difficulty from most recent block (already calculated above)
  const networkDifficulty = latestBlock?.difficulty || 0;

  // Format last refresh time
  const lastRefreshFormatted = lastRefresh
    ? format(lastRefresh, 'HH:mm:ss')
    : 'Never';

  return (
    <div className="bg-navy-light border border-dark p-4 md:p-6 rounded space-y-4">
      <h2 className="text-primary text-xl font-bold mb-4">Statistics</h2>

      {/* Time Since Last Block */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Time Since Last Block <MetricTooltip metricKey="timeSinceLastBlock" /></p>
        <p className="text-accent-orange text-xl md:text-2xl font-bold">{elapsedTime.formatted}</p>
      </div>

      {/* Expected Block Time */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Expected Time <MetricTooltip metricKey="expectedTime" /></p>
        <p className="text-primary text-base md:text-lg">{formatTimeInterval(analysis.expectedTime)}</p>
      </div>

      {/* Median Time to Next Block */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Median Time to Next Block <MetricTooltip metricKey="medianTime" /></p>
        <p className="text-accent-orange text-base md:text-lg font-bold">{formatTimeInterval(analysis.medianTime)}</p>
        <p className="text-primary text-xs mt-1">50% probability threshold</p>
      </div>

      {/* Estimated Time to Next Block */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Est. Time to Next Block <MetricTooltip metricKey="estimatedTimeLeft" /></p>
        <div className="flex items-center gap-2">
          <p
            className={`text-base md:text-lg font-bold ${
              isOverdue ? 'text-red-500' : 'text-accent-orange'
            }`}
          >
            {estimatedTimeLeftFormatted}
          </p>
          {isOverdue && (
            <span className="text-red-500 text-xs font-semibold">(overdue)</span>
          )}
        </div>
      </div>

      {/* Deviation from Expected */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Historical Z-Score <MetricTooltip metricKey="zScore" /></p>
        <p className="text-primary text-xs mb-2">Past performance metric</p>
        <p
          className={`text-base md:text-lg font-bold ${
            analysis.deviation?.pValue < 0.05 ? 'text-accent-orange' : 'text-primary'
          }`}
        >
          Z-score: {analysis.deviation?.zScore?.toFixed(2) || 'N/A'}
        </p>
        <p className="text-primary text-sm">
          P-value: {((analysis.deviation?.pValue || 0) * 100).toFixed(2)}%
        </p>
      </div>

      {/* Next Block Probabilities */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Next Block Probabilities <MetricTooltip metricKey="nextBlockProbabilities" /></p>
        {predictionData?.nextBlockProbabilities && (
          <div className="space-y-1 text-sm">
            <p className="text-primary">
              1h: <span className="text-accent-orange">{formatProbability(predictionData.nextBlockProbabilities.within1h)}</span> <MetricTooltip metricKey="probability1h" position="right" />
            </p>
            <p className="text-primary">
              2h: <span className="text-accent-orange">{formatProbability(predictionData.nextBlockProbabilities.within2h)}</span> <MetricTooltip metricKey="probability2h" position="right" />
            </p>
            <p className="text-primary">
              4h: <span className="text-accent-orange">{formatProbability(predictionData.nextBlockProbabilities.within4h)}</span> <MetricTooltip metricKey="probability4h" position="right" />
            </p>
            <p className="text-primary">
              8h: <span className="text-accent-orange">{formatProbability(predictionData.nextBlockProbabilities.within8h)}</span> <MetricTooltip metricKey="probability8h" position="right" />
            </p>
          </div>
        )}
      </div>

      {/* Percentiles */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Confidence Intervals <MetricTooltip metricKey="confidenceIntervals" /></p>
        <div className="space-y-1 text-sm">
          {Array.isArray(analysis.timePercentiles) && analysis.timePercentiles.length >= 4 ? (() => {
            const [p50, p80, p90, p95] = analysis.timePercentiles;
            return (
              <>
                {Number.isFinite(p50) && (
                  <p className="text-primary">
                    50%: <span className="text-accent-orange">{formatTimeInterval(p50)}</span> <MetricTooltip metricKey="percentile50" position="right" />
                  </p>
                )}
                {Number.isFinite(p80) && (
                  <p className="text-primary">
                    80%: <span className="text-accent-orange">{formatTimeInterval(p80)}</span> <MetricTooltip metricKey="percentile80" position="right" />
                  </p>
                )}
                {Number.isFinite(p90) && (
                  <p className="text-primary">
                    90%: <span className="text-accent-orange">{formatTimeInterval(p90)}</span> <MetricTooltip metricKey="percentile90" position="right" />
                  </p>
                )}
                {Number.isFinite(p95) && (
                  <p className="text-primary">
                    95%: <span className="text-accent-orange">{formatTimeInterval(p95)}</span> <MetricTooltip metricKey="percentile95" position="right" />
                  </p>
                )}
              </>
            );
          })() : (
            <p className="text-primary text-xs">Percentile data unavailable</p>
          )}
        </div>
      </div>

      {/* Observed vs Expected Blocks */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Recent Performance</p>
        <p className="text-primary text-sm">
          Observed: <span className="text-accent-orange">{analysis.observedBlocks}</span> <MetricTooltip metricKey="observedBlocks" position="right" />
        </p>
        <p className="text-primary text-sm">
          Expected: <span className="text-accent-orange">{analysis.expectedBlocks.toFixed(2)}</span> <MetricTooltip metricKey="expectedBlocks" position="right" />
        </p>
        <p className="text-primary text-sm">
          Historical Growth Factor: <span className="text-accent-orange">{analysis.growthFactor.toFixed(2)}</span> <MetricTooltip metricKey="growthFactor" position="right" />
        </p>
      </div>

      {/* Model Accuracy (Backtesting) */}
      {backtestResults && (
        <div className="bg-navy border border-dark p-3 md:p-4 rounded">
          <div 
            className="flex items-center justify-between cursor-pointer mb-2"
            onClick={() => setShowAccuracy(!showAccuracy)}
          >
            <p className="text-primary text-sm font-semibold">Model Accuracy</p>
            <span className="text-primary text-xs">{showAccuracy ? '▼' : '▶'}</span>
          </div>
          
          {showAccuracy && (
            <div className="space-y-3">
              {/* Aggregate Metrics */}
              <div className="space-y-1 text-sm">
                <p className="text-primary">
                  MAE: <span className="text-accent-orange">{formatTimeInterval(backtestResults.metrics.mae)}</span> <MetricTooltip metricKey="mae" position="right" />
                </p>
                <p className="text-primary">
                  RMSE: <span className="text-accent-orange">{formatTimeInterval(backtestResults.metrics.rmse)}</span> <MetricTooltip metricKey="rmse" position="right" />
                </p>
                <p className="text-primary">
                  Calibration: <span className={`font-bold ${
                    backtestResults.metrics.calibration >= 45 && backtestResults.metrics.calibration <= 55
                      ? 'text-green-500'
                      : 'text-accent-orange'
                  }`}>
                    {backtestResults.metrics.calibration.toFixed(1)}%
                  </span>
                  <span className="text-xs text-primary ml-1">(target: ~50%)</span> <MetricTooltip metricKey="calibration" position="right" />
                </p>
                <p className="text-primary text-xs">
                  Predictions: {backtestResults.metrics.totalPredictions}
                </p>
              </div>
              
              {/* Error Chart (Recent 100) */}
              {backtestResults.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-primary text-xs mb-2">Recent Prediction Errors (hours)</p>
                  <div className="w-full h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={backtestResults.errors.slice(-100).map((e, idx) => ({
                          index: idx,
                          errorHours: e.error / 3600,
                        }))}
                        margin={{ top: 5, right: 5, bottom: 5, left: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#1a2332" />
                        <XAxis 
                          dataKey="index" 
                          stroke="#ffffff" 
                          tick={{ fill: '#ffffff', fontSize: 10 }} 
                          hide
                        />
                        <YAxis 
                          stroke="#ffffff" 
                          tick={{ fill: '#ffffff', fontSize: 10 }}
                          width={30}
                        />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#0a1929', border: '1px solid #000' }}
                          labelStyle={{ color: '#ffffff' }}
                          itemStyle={{ color: '#ff8c42' }}
                        />
                        <ReferenceLine y={0} stroke="#ffffff" strokeDasharray="3 3" />
                        <Line 
                          type="monotone" 
                          dataKey="errorHours" 
                          stroke="#ff8c42" 
                          strokeWidth={1.5}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
              
              {/* Worst Predictions Table */}
              {backtestResults.errors.length > 0 && (
                <div className="mt-3">
                  <p className="text-primary text-xs mb-2">Worst 5 Predictions</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-dark">
                          <th className="text-left text-primary py-1">Height</th>
                          <th className="text-right text-primary py-1">Actual</th>
                          <th className="text-right text-primary py-1">Predicted</th>
                          <th className="text-right text-primary py-1">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...backtestResults.errors]
                          .sort((a, b) => b.absError - a.absError)
                          .slice(0, 5)
                          .map((e, idx) => (
                            <tr key={idx} className="border-b border-dark">
                              <td className="text-primary py-1">{e.blockHeight}</td>
                              <td className="text-right text-primary py-1">
                                {formatTimeInterval(e.actualInterval)}
                              </td>
                              <td className="text-right text-primary py-1">
                                {formatTimeInterval(e.predictedMedian)}
                              </td>
                              <td className="text-right text-accent-orange py-1 font-semibold">
                                {e.error > 0 ? '+' : ''}{formatTimeInterval(Math.abs(e.error))}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pool Hashrate & Network Info */}
      <div className="bg-navy border border-dark p-3 md:p-4 rounded">
        <p className="text-primary text-sm mb-2">Pool & Network</p>
        <p className="text-primary text-sm">
          Hashrate: <span className="text-accent-orange">{poolInfo.hashrateEH.toFixed(2)} EH/s</span> <MetricTooltip metricKey="poolHashrate" position="right" />
        </p>
        <p className="text-primary text-sm">
          Network Share: <span className="text-accent-orange">{poolInfo.hashrateSharePercent.toFixed(2)}%</span> <MetricTooltip metricKey="networkShare" position="right" />
        </p>
        {networkDifficulty > 0 && (
          <p className="text-primary text-sm">
            Difficulty: <span className="text-accent-orange">{networkDifficulty.toLocaleString()}</span> <MetricTooltip metricKey="networkDifficulty" position="right" />
          </p>
        )}
      </div>

      {/* Last Refresh */}
      <div className="pt-4 border-t border-dark">
        <p className="text-primary text-xs text-center">
          Last updated: {lastRefreshFormatted}
        </p>
      </div>
    </div>
  );
}

// Memoize StatsPanel to prevent unnecessary re-renders
export const StatsPanel = React.memo(StatsPanelComponent, (prevProps, nextProps) => {
  // Custom comparison - compare key properties
  // Since we're using context, we compare the actual values
  // This comparison will be checked when context updates
  return false; // Always allow re-render and let React.memo handle shallow comparison
});
